<template>
  <div>
    <!-- Header -->
    <base-header class="pb-6 pb-1 pt-5 pt-md-8 bg-gradient-custom" type="custom">
      <b-row>
        <b-col xl="3" md="6">
          <base-button icon type="primary" @click="redirectToForm">
            <span class="btn-inner--icon"><i class="ni ni-fat-add"></i></span>
            <span class="btn-inner--text">Nuevo Plan</span>
          </base-button>
        </b-col>
      </b-row>
    </base-header>

    <!-- Tabla de Planes -->
    <b-container fluid class="mt--4">
      <ReusableTable
        :title="tableTitle"
        :data="dataPLAN"
        :columns="tableColumns"
        :actions="tableActions"
        :per-page="itemsPerPage"
        :total="totalPLAN"
        :search-query="searchQuery"
        @fetch-data="listadoPlan"
        @search="onSearch"
      >
        <!-- Slot para el ID del Plan -->
        <template #column-ID_PLAN="{ row }">
          <span class="font-weight-bold">{{ row.ID_PLAN }}</span>
        </template>

        <!-- Slots para los iconos de las acciones -->
        <template #icon-edit>
          <i class="fas fa-pencil-alt"></i>
        </template>
        <template #icon-delete>
          <i class="far fa-trash-alt"></i>
        </template>
      </ReusableTable>
    </b-container>

    <!-- Modal de Confirmación de Eliminación -->
    <b-modal v-model="showDeleteModal" title="Confirmar Eliminación" size="md">
      <div class="py-3 text-center">
        <i class="far fa-trash-alt ni-3x text-red"></i>
        <p v-if="planToDelete">
          ¿Estás seguro de eliminar este plan:
          <strong>{{ planToDelete.ID_PLAN }}</strong>?
        </p>
        <p v-else>¿Estás seguro de eliminar este plan?</p>
      </div>
      <template v-slot:modal-footer>
        <b-button variant="danger" @click="confirmDelete">Eliminar</b-button>
        <b-button variant="secondary" @click="showDeleteModal = false">
          Cancelar
        </b-button>
      </template>
    </b-modal>

    <!-- Modal de Token Expirado -->
   <token-expired-modal :show="showTokenExpiredModal"> </token-expired-modal>
  </div>
</template>

<script>
import axios from "axios";
import ReusableTable from "./Tables/RegularTables/TablaReu.vue";
import Modal from "../components/Modal.vue";
import Notifications from "../components/NotificationPlugin/Notifications.vue";
import TokenExpiredModal from '../components/TokenExpiredModal.vue';

export default {
  components: { ReusableTable, Modal, Notifications, TokenExpiredModal },
  data() {
    return {
      dataPLAN: [], // Datos de la tabla
      totalPLAN: 0, // Total de planes
      showDeleteModal: false, // Controla la visibilidad del modal de eliminación
      planToDelete: null, // Plan seleccionado para eliminar
      isLoading: true, // Estado de carga
      showTokenExpiredModal: false, // Controla la visibilidad del modal de token expirado
      itemsPerPage: 10, // Elementos por página
      currentPage: 1, // Página actual
      searchQuery: "", // Consulta de búsqueda
      tableTitle: "Listado de Planes", // Título de la tabla
      tableColumns: [
        { prop: "ID_PLAN", label: "ID" },
        { prop: "ICCID", label: "ICCID" },
        { prop: "PRECIO", label: "Precio" },
        { prop: "ANUAL_MENSUAL", label: "Anual/Mensual" },
        { prop: "DATA_PROMETIDA", label: "Data Prometida" },
      ], // Columnas de la tabla
      tableActions: [
        {
          name: "edit",
          label: "Editar",
          class: "btn text-black",
          onClick: (row) => this.$router.push(`/PlanEdit/${row.ID_PLAN}`),
        },
        {
          name: "delete",
          label: "Eliminar",
          class: "btn text-red",
          onClick: (row) => this.openDeleteModal(row),
        },
      ], // Acciones de la tabla
    };
  },
  watch: {
    // Observar cambios en la página actual
    currentPage() {
      this.listadoPlan();
    },
    // Observar cambios en la consulta de búsqueda
    searchQuery() {
      this.currentPage = 1; // Reiniciar a la primera página al buscar
      this.listadoPlan();
    },
  },
  methods: {
    // Obtener la lista de planes
    async listadoPlan({ page, perPage, search } = {}) {
      this.isLoading = true;
      try {
        const token = localStorage.getItem("token");
        const response = await axios.get(
          `${process.env.VUE_APP_API_URL}/obtenerDatosPlan`,
          {
            params: {
              page: page || this.currentPage,
              perPage: perPage || this.itemsPerPage,
              search: search || this.searchQuery,
            },
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (response.data.success) {
          this.dataPLAN = response.data.data;
          this.totalPLAN = response.data.total;
          this.currentPage = page || this.currentPage; // Actualizar la página actual
        } else {
          this.$notifications.addNotification({
            type: "danger",
            message: "Error al obtener los datos.",
          });
        }
      } catch (error) {
        if (error.response && error.response.status === 401) {
          this.handleTokenExpired();
        } else {
          this.$notifications.addNotification({
            type: "danger",
            message: "Error en la solicitud.",
          });
        }
      } finally {
        this.isLoading = false;
      }
    },
    // Manejar la búsqueda
    onSearch(searchQuery) {
      this.searchQuery = searchQuery;
      this.listadoPlan();
    },
    // Abrir el modal de eliminación
    openDeleteModal(row) {
      this.planToDelete = row;
      this.showDeleteModal = true;
    },
    // Confirmar la eliminación de un plan
    async confirmDelete() {
  if (!this.planToDelete) return;

  try {
    const token = localStorage.getItem("token");
    if (!token) {
      this.handleTokenExpired(); // Si no hay token, redirige al login
      return;
    }

    const response = await axios.delete(
      `${process.env.VUE_APP_API_URL}/eliminarPLAN/${this.planToDelete.ID_PLAN}`,
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );

    if (response.data.success) {
      this.$notifications.addNotification({
        type: "success",
        message: "Plan eliminado correctamente.",
      });

      if (this.dataPLAN.length === 1 && this.currentPage > 1) {
        this.currentPage -= 1;
      }
      // Recargar la lista de planes
      this.listadoPlan();
    } else {
      this.$notifications.addNotification({
        type: "danger",
        message: response.data.message || "Error al eliminar el plan.",
      });
    }
  } catch (error) {
    if (error.response && error.response.status === 401) {
      console.error("Error 401: Token no válido o expirado", error.response.data);
      this.handleTokenExpired();
    } else {
      console.error("Error al eliminar el plan:", error);
      this.$notifications.addNotification({
        type: "danger",
        message: "No se pudo eliminar el plan.",
      });
    }
  } finally {
    this.showDeleteModal = false;
    this.planToDelete = null;
  }
},
    // Redirigir al formulario de creación
    redirectToForm() {
      this.$router.push("/PlanAdd");
    },
    // Manejar token expirado
    handleTokenExpired() {
      this.showTokenExpiredModal = true;
      setTimeout(() => {
        this.$router.push("/login");
      }, 3000);
    },
  },
  mounted() {
    // Cargar la lista de planes al montar el componente
    this.listadoPlan();
  },
};
</script>